import { useEffect, useEffectEvent, useRef } from 'react';

export type UsePollingLifecycleParams = {
  pollInterval: number | undefined;
  skip: boolean | undefined;
  error: unknown;
  stopOnError: boolean;
  refetchOnVisible: boolean;
  refetch: () => Promise<unknown>;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
};

/**
 * Stops an Apollo poll on error and while the tab is hidden, and restarts it
 * after a later success or when the tab becomes visible. Does nothing when
 * `pollInterval` is 0 or unset.
 */
export const usePollingLifecycle = ({
  pollInterval,
  skip,
  error,
  stopOnError,
  refetchOnVisible,
  refetch,
  startPolling,
  stopPolling,
}: UsePollingLifecycleParams): void => {
  const stoppedDueToErrorRef = useRef(false);
  const handledErrorRef = useRef<unknown>(null);

  const stopPollingOnError = useEffectEvent(() => {
    stoppedDueToErrorRef.current = true;
    stopPolling();
  });

  const resumePollingAfterError = useEffectEvent(() => {
    stoppedDueToErrorRef.current = false;
    handledErrorRef.current = null;
    if (pollInterval && !document.hidden) {
      startPolling(pollInterval);
    }
  });

  const onVisibilityChange = useEffectEvent(() => {
    if (!pollInterval) {
      return;
    }
    if (document.hidden) {
      stopPolling();
      return;
    }
    stoppedDueToErrorRef.current = false;
    handledErrorRef.current = null;
    if (refetchOnVisible) {
      refetch().catch(() => undefined);
    }
    startPolling(pollInterval);
  });

  useEffect(() => {
    if (!pollInterval) {
      return;
    }
    if (error && stopOnError) {
      if (error !== handledErrorRef.current) {
        handledErrorRef.current = error;
        stopPollingOnError();
      }
    } else if (!skip && stoppedDueToErrorRef.current) {
      resumePollingAfterError();
    }
  }, [error, stopOnError, pollInterval, skip]);

  const stopPollingIfHidden = useEffectEvent(() => {
    if (document.hidden) {
      stopPolling();
    }
  });

  useEffect(() => {
    if (skip || !pollInterval) {
      return;
    }

    stopPollingIfHidden();
    const handleVisibilityChange = () => onVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [skip, pollInterval]);
};
